import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { Moment } from 'moment';
import { HttpHeaders } from '@angular/common/http';
import { Sort } from '@angular/material/sort';
import { MultiDateRangeQuery } from '../../components/app-layout/shared/report-filters/multi-date-range/multi-date-range-query';

export interface AccountRegisterFilters
{
    assets: AccountRegisterFilterOption[];
    liabilities: AccountRegisterFilterOption[];
    equities: AccountRegisterFilterOption[];
    incomeCategories: AccountRegisterFilterOption[];
    expenseCategories: AccountRegisterFilterOption[];
}

export interface AccountRegisterFilterOption
{
    accountType: string;
    id: number;
    display: string;
    children?: AccountRegisterFilterOption[];
}

@Injectable({
    providedIn: 'root',
})
export class ReportsService
{
    private _http: ApplicationHttpClient;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /**
     * @returns {Observable}
     */
    public getHomepageStats(): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsHomepage(),
        );

        return this._http.get(url);
    }

    /** Method to pull stats as a number to display on landing pages */
    public getSignupStats(): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Stats,
        );

        return this._http.get(url);
    }

    public getPdf(url: string) {
        const newHeaders = new HttpHeaders({
            'Content-Type': 'application/pdf',
        });

        const options: Object = {
            headers: newHeaders,
            responseType: 'arraybuffer',
        };

        return this._http.get(url, options);
    }

    public getCsv(url: string) {
        const newHeaders = new HttpHeaders({
            'Content-Type': 'text/csv',
        });

        const options: Object = {
            headers: newHeaders,
            responseType: 'blob',
        };

        return this._http.get(url, options);
    }

    /**
     * To post with body data, but receive back an application/pdf type, using 'blob' for responseType works.
     * Otherwise the body data appears to send but never makes it to the controller endpoint since it was expecting
     * content type application/pdf and ignored the json string request.
     *
     * Bug that discovered this was https://trello.com/c/rGQ5Nlcj/1920-bug-unit-balance-summary-report
     *
     * https://stackoverflow.com/questions/53731136/making-a-post-request-that-returns-a-pdf-using-angulars-httpclient
     *
     * @param url
     * @param data
     */
    public postPdf(url: string, data: {}) {
        return this._http.post(url, this._http.serializeObject(data), {responseType: 'blob'});
    }

    /**
     * @param pdf
     * @param filter
     */
    public getBankReconciliationReport(pdf: boolean, filter: { reconciliation: number }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filter);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportReconciliations(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getBookkeepingProspectsReport(format: string) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsBookkeepingProspects(
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, {});
        }
        return this._http.post(url, this._http.serializeObject({}));
    }

    public getNonbilledOrgsReport(format: string, data: {
        month: number,
        year: number
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsNonbilled(
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, data);
        }
        return this._http.post(url, this._http.serializeObject(data));
    }

    public getOrganizationsReport(format: string) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsOrganizations(
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, {});
        }
        return this._http.post(url, this._http.serializeObject({}));
    }

    public getMasterBillingReport(format: string) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsMasterBilling(
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, {});
        }
        return this._http.post(url, this._http.serializeObject({}));
    }

    public getApiPerformanceReport(format: string) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsApiPerformance(
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, {});
        }
        return this._http.post(url, this._http.serializeObject({}));
    }

    public getChurnReport(format: string, filters: {
        startDate: Moment;
        endDate: Moment;
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsChurn(
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, filters);
        }
        return this._http.post(url, this._http.serializeObject(filters));
    }

    public getNewCustomersReport(format: string, filters: {
        startDate: Moment,
        endDate: Moment,
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsNewCustomers(
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, filters);
        }
        return this._http.post(url, this._http.serializeObject(filters));
    }

    public getAccountRegister(format: string, filters: {
        accounts: string,
        startDate: Moment;
        endDate: Moment;
        pageSize: number;
        page: number;
        showMemoColumn: boolean;
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsAccountRegister(
                        jwt.orgId,
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, filters);
        }
        return this._http.post(url, this._http.serializeObject(filters));
    }

    public getAccountRegisterFilters(): Observable<AccountRegisterFilters> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsAccountRegisterFilters(
                        jwt.orgId,
                ),
        );

        return this._http.get(url);
    }

    public checkBatchStatus(batchId: string) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl('/batch-status/' + batchId);

        return this._http.post(url, this._http.serializeObject({}));
    }

    public generateGeneralLedger() {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsGenerateGeneralLedger(jwt.orgId),
        );

        return this._http.post(url, this._http.serializeObject({}));
    }

    public getGeneralLedger(
            format: string,
            filters: {
                startDate: Moment;
                endDate: Moment;
                pageSize: number;
                page: number;
            },
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsGeneralLedger(
                        jwt.orgId,
                        format,
                ),
        );

        if (format !== 'json') {
            return this.postPdf(url, filters);
        }
        return this._http.post(url, this._http.serializeObject(filters));
    }

    public getBalanceSheetDetails(pdf: boolean, filter: { asOfDate: Moment }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filter);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsBalanceSheetDetails(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getProfitVsLossPercentage(
            pdf: boolean,
            filters: {
                startDate: Moment;
                endDate: Moment;
            },
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsProfitVsLossPercentage(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getProfitVsLossSummary(
            pdf: boolean,
            filters: {
                startDate: Moment;
                endDate: Moment;
            },
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsProfitVsLossSummary(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getProfitVsLossDetail(
            pdf: boolean,
            filters: {
                startDate: Moment,
                endDate: Moment,
            },
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsProfitVsLossDetail(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    /**
     * @returns {Observable}
     * @param pdf
     * @param filters
     */
    public getProfitVsLossByMonth(
            pdf: boolean,
            filters: {
                startDate: Moment;
                endDate: Moment;
            },
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsProfitVsLossByMonth(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getBudgetReport(mode: 'summary' | 'yearly' | 'monthly', pdf: boolean, filters: {
        startDate: Moment;
        endDate: Moment;
        withZeros: boolean;
    }): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsBudgetSummary(
                        jwt.orgId,
                        mode,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }

        return this._http.get(url);
    }

    /**
     * @returns {Observable}
     * @param pdf
     * @param filters
     */
    public getBudgetVsActualSummary(
            pdf: boolean,
            filters: {
                startDate: Moment;
                endDate: Moment;
                withZeros: boolean;
            },
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsBudgetVsActualSummary(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }

        return this._http.get(url);
    }

    /**
     * @returns {Observable}
     * @param pdf
     * @param filters
     */
    public getBudgetVsActualSplitSummary(
            pdf: boolean,
            filters: MultiDateRangeQuery,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsBudgetVsActualSummary(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }

        return this._http.get(url);
    }

    public getProfitVsLossComparisonSummary(
            pdf: boolean,
            filters: MultiDateRangeQuery,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsProfitVsLossSummary(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    /**
     * @param pdf
     * @param filters
     */
    public getBudgetPerformanceByMonth(
            pdf: boolean,
            filters: {
                month: number;
                year: number;
                basis: string;
            },
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsBudgetVsActualByMonth(
                        jwt.orgId,
                        pdf,
                ) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }

        return this._http.get(url);
    }

    /**
     * @returns {Observable}
     * @param filters
     * @param format
     */
    public getJournalEntries(
            filters: {
                startDate: Moment;
                endDate: Moment;
            },
            format?: string,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsJournalEntries(
                        jwt.orgId,
                        format,
                ) + query,
        );

        if (format === 'pdf') {
            return this.getPdf(url);
        } else if (format === 'csv') {
            return this.getCsv(url);
        }

        return this._http.get(url);
    }

    /**
     * @returns {Observable}
     * @param pdf
     * @param sort
     */
    public getUnitContactInfo(pdf: boolean, sort: Sort = null): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsContactInfo(jwt.orgId, pdf),
        );

        if (sort) {
            url += '?sort=' + sort.active;
            url += '&direction=' + sort.direction;
        }

        if (pdf) {
            return this.getPdf(url);
        }

        return this._http.get(url);
    }

    /**
     * @returns {Observable}
     * @param pdf
     * @param sort
     */
    public getVendorInfo(pdf: boolean, sort: Sort = null): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsVendorInfo(jwt.orgId, pdf),
        );

        if (sort) {
            url += '?sort=' + sort.active;
            url += '&direction=' + sort.direction;
        }

        if (pdf) {
            return this.getPdf(url);
        }

        return this._http.get(url);
    }

    /**
     * @returns {Observable}
     * @param pdf
     * @param filters
     */
    public getOwnershipChange(pdf: boolean, filters: {
        startDate: Moment;
        endDate: Moment;
    }): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsOwnershipChange(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }

        return this._http.get(url);
    }

    /**
     * @returns {Observable}
     * @param pdf
     * @param filters
     */
    public getAgingOfAccounts(pdf: boolean, filters: { asOfDate: Moment }): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsAgingOfAccounts(jwt.orgId, pdf),
        );

        if (pdf) {
            return this.postPdf(url, filters);
        }

        return this._http.post(url, this._http.serializeObject(filters));
    }

    /**
     * @param {string} fieldList
     * @param filterUnitIds
     * @param pdf
     * @param landscape
     * @param sort
     * @returns {Observable<Object>}
     */
    public getCustomReport(
            fieldList: string[],
            filterUnitIds: number[],
            pdf: boolean,
            landscape: boolean = false,
            sort: Sort = null,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.ReportsCustom(jwt.orgId, pdf));

        const request = {
            fields: fieldList,
            unitIds: filterUnitIds,
            landscape: landscape,
            sort: null,
            direction: null,
        };

        if (sort) {
            request.sort = sort.active;
            request.direction = sort.direction;
        }

        if (pdf) {
            return this.postPdf(url, request);
        }

        return this._http.post(url, this._http.serializeObject(request));
    }

    public getValidFieldsForVendorOverview() {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsVendorOverviewFields(jwt.orgId),
        );

        return this._http.get(url);
    }

    public getValidFieldsForCustomReport(
            organizationId: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsCustomFields(organizationId || jwt.orgId),
        );

        return this._http.get(url);
    }

    public getVendorOverview(pdf: boolean, filters: {
        startDate: Moment,
        endDate: Moment,
        fields: string[]
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsVendorOverview(jwt.orgId, pdf),
        );

        if (pdf) {
            return this.postPdf(url, filters);
        }
        return this._http.post(url, this._http.serializeObject(filters));
    }

    public getExpensesByVendor(pdf: boolean, filters: {
        startDate: Moment,
        endDate: Moment
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsExpensesByVendor(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getSubscriptionHistory(pdf: boolean, filters: {
        startDate: Moment;
        endDate: Moment;
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsSubscriptionHistory(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getExpensesByCategory(pdf: boolean, filters: {
        startDate: Moment;
        endDate: Moment;
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsExpensesByCategory(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getExpensesDetail(pdf: boolean, filters: {
        startDate: Moment;
        endDate: Moment;
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsExpensesDetail(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getReceiptsDetail(pdf: boolean, filters: {
        startDate: Moment,
        endDate: Moment,
        check: boolean,
        cash: boolean,
        bank: boolean,
        card: boolean,
        credit: boolean
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsReceiptsDetail(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getPaymentsByUnit(pdf: boolean, filters: {
        startDate: Moment,
        endDate: Moment,
        check: boolean,
        cash: boolean,
        bank: boolean,
        card: boolean,
        credit: boolean
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsPaymentsByUnit(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getNewRecurringCharges(format: string, filters: {
        startDate: Moment;
        endDate: Moment;
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsNewRecurringCharges(jwt.orgId, format) + query,
        );

        if (format !== 'json') {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getPrepaymentsByUnit(pdf: boolean, filters: { asOfDate: Moment }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsPrepaymentsByUnit(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getUnitBalanceList(
            pdf: boolean,
            unitIds: number[],
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportUnitBalanceList(jwt.orgId, pdf),
        );

        if (pdf) {
            return this.postPdf(url, {units: unitIds});
        }
        return this._http.post(url, this._http.serializeObject({units: unitIds}));
    }

    public getUnitBalanceSummary(
            pdf: boolean,
            filters: {
                startDate: Moment;
                endDate: Moment;
            },
            unitIds: number[],
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportUnitBalanceSummary(jwt.orgId, pdf),
        );

        if (pdf) {
            return this.postPdf(url, {units: unitIds, ...filters});
        }
        return this._http.post(url, this._http.serializeObject({units: unitIds, ...filters}));
    }

    public getUnitBalanceDetail(
            pdf: boolean,
            filters: {
                startDate: Moment;
                endDate: Moment;
            },
            unitIds: number[],
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportUnitBalanceDetail(jwt.orgId, pdf),
        );

        if (pdf) {
            return this.postPdf(url, {units: unitIds, ...filters});
        }
        return this._http.post(url, this._http.serializeObject({units: unitIds, ...filters}));
    }

    public getDelinquentAccountSummary(pdf: boolean, filters: { doBuildAnonymous: boolean }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportDelinquentAccountsSummary(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getDelinquentAccountsDetail(pdf: boolean, filters: { doBuildAnonymous: boolean }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportDelinquentAccountsDetail(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getInvoicesByUnit(pdf: boolean, filters: {
        startDate: Moment;
        endDate: Moment;
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportInvoicesByUnit(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getInvoicesByRevenueCategory(
            pdf: boolean,
            filters: {
                startDate: Moment;
                endDate: Moment;
            },
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportInvoicesByRevenueCategory(jwt.orgId, pdf) +
                query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getInvoicesByTitle(pdf: boolean, filters: {
        startDate: Moment;
        endDate: Moment;
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportInvoicesByTitle(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    /**
     * @returns {Observable<PaymentGroup[]>}
     * @param pdf
     * @param filters
     */
    public getReturnedPayments(pdf: boolean, filters: {
        startDate: Moment,
        endDate: Moment,
        check: boolean,
        cash: boolean,
        bank: boolean,
        card: boolean,
        credit: boolean
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportReturnedPayments(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }

        return this._http.get(url);
    }

    public getDepositDetails(
            pdf: boolean,
            filters: {
                startDate: Moment,
                endDate: Moment,
                payhoaDeposits: boolean,
                lockboxDeposits: boolean,
                offlineDeposits: boolean,
                otherDeposits: boolean
            },
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportDepositDetails(jwt.orgId, pdf) + query,
        );

        if (pdf) {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getViolationsReport(
            format: string,
            filters: {
                startDate: Moment,
                endDate: Moment,
                status: string,
            },
    ) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportViolations(jwt.orgId, format) + query,
        );

        if (format !== 'json') {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }

    public getCheckRegister(
            format: string,
            filters: {
                startDate: Moment;
                endDate: Moment;
            },
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ReportsCheckRegister(jwt.orgId, format) + query,
        );

        if (format !== 'json') {
            return this.getPdf(url);
        }
        return this._http.get(url);
    }
}
